import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Menu, X, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Logo } from "@/components/brand/Logo";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { SecondaryButton } from "@/components/brand/SecondaryButton";
import { useUserRoles } from "@/hooks/useUserRoles";
import { ALLOW_ON_DOMAIN_BOOKING, APPROVED_CONSULT_URL, CONSULT_URL_IS_EXTERNAL, CONSULT_CTA_LABEL, isLevel } from "@/config/compliance";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const NAV_LINKS = [
  { to: "/solutions", label: "Solutions" },
  { to: "/family-capital-system", label: "The System" },
  { to: "/academy", label: "Academy" },
  { to: "/about", label: "Our Team" },
  { to: "/membership", label: "Membership" },
  { to: "/contact", label: "Contact" },
] as const;

const TABLET_PRIMARY = [
  { to: "/solutions", label: "Solutions" },
  { to: "/family-capital-system", label: "The System" },
  { to: "/academy", label: "Academy" },
  { to: "/membership", label: "Membership" },
] as const;

const TABLET_MORE = [
  { to: "/about", label: "Our Team" },
  { to: "/contact", label: "Contact" },
] as const;

const LINK_BASE =
  "relative inline-flex items-center h-16 font-sans font-semibold uppercase tracking-[0.16em] text-cream-50/85 transition-colors hover:text-bronze-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bronze-400 focus-visible:ring-offset-4 focus-visible:ring-offset-navy-900 after:absolute after:left-0 after:right-0 after:bottom-3 after:h-[2px] after:bg-transparent hover:after:bg-bronze-400/60 after:transition-colors";
const LINK_SIZE_DESKTOP = "text-[12.5px] xl:text-[13px]";
const LINK_SIZE_TABLET = "text-[12px]";
const LINK_ACTIVE = "text-bronze-400 after:bg-bronze-400";

export function Navbar() {
  const [open, setOpen] = useState(false);
  const { userId, isAdmin, isAgent, isClient, portalCount } = useUserRoles();

  const portalHref =
    portalCount > 1
      ? "/account"
      : isAdmin
        ? "/admin"
        : isAgent
          ? "/agent"
          : isClient
            ? "/client"
            : "/account";
  const portalLabel =
    portalCount > 1 ? "My Portal" : isAdmin ? "Admin Console" : isAgent ? "Strategist Console" : "Client Portal";

  return (
    <header
      className="fixed inset-x-0 top-0 z-50 bg-navy-900/95 backdrop-blur border-b border-bronze-600/40"
    >
      <div className="grid h-16 grid-cols-[minmax(0,1fr)_auto] items-center gap-2 overflow-x-hidden pl-4 pr-4 md:pl-6 md:pr-6 lg:flex lg:justify-between lg:gap-0 lg:pl-8 lg:pr-8">
        {/* LEFT — Logo */}
        <Link
          to="/"
          aria-label="Currency Thoughts home"
          className="min-w-0 lg:shrink-0 lg:mr-8 xl:mr-12"
        >
          <Logo variant="light" />
        </Link>

        {/* CENTER — primary 4 + More (lg+) */}
        <nav className="hidden lg:flex flex-1 min-w-0 items-center justify-center gap-3 xl:gap-5">
          {TABLET_PRIMARY.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={cn(LINK_BASE, LINK_SIZE_TABLET)}
              activeProps={{ className: cn(LINK_BASE, LINK_SIZE_TABLET, LINK_ACTIVE) }}
            >
              {link.label}
            </Link>
          ))}
          <Popover>
            <PopoverTrigger
              className={cn(
                LINK_BASE,
                LINK_SIZE_TABLET,
                "gap-1 data-[state=open]:text-bronze-400",
              )}
            >
              More
              <ChevronDown className="h-3.5 w-3.5" aria-hidden />
            </PopoverTrigger>
            <PopoverContent
              align="end"
              sideOffset={4}
              className="w-44 rounded-[4px] bg-navy-800 p-2 ring-1 ring-bronze-600 border-0 text-cream-50"
            >
              <div className="flex flex-col">
                {TABLET_MORE.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="rounded-[2px] px-3 py-2 font-sans text-[12px] font-semibold uppercase tracking-[0.16em] text-cream-50 hover:bg-navy-700 hover:text-bronze-400 transition-colors"
                    activeProps={{ className: "rounded-[2px] px-3 py-2 font-sans text-[12px] font-semibold uppercase tracking-[0.16em] text-bronze-400 bg-navy-700" }}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </nav>

        {/* RIGHT — utility cluster */}
        <div className="hidden lg:flex items-center gap-3 lg:gap-6 shrink-0">
          {userId ? (
            <Link
              to={portalHref}
              className={cn(LINK_BASE, "text-[12px] lg:text-[12.5px] xl:text-[13px]")}
              activeProps={{ className: cn(LINK_BASE, "text-[12px] lg:text-[12.5px] xl:text-[13px]", LINK_ACTIVE) }}
            >
              {portalLabel}
            </Link>
          ) : (
            <Link
              to="/login"
              className={cn(LINK_BASE, "text-[12px] lg:text-[12.5px] xl:text-[13px]")}
              activeProps={{ className: cn(LINK_BASE, "text-[12px] lg:text-[12.5px] xl:text-[13px]", LINK_ACTIVE) }}
            >
              Client Login
            </Link>
          )}
          <span aria-hidden className="hidden lg:block h-5 w-px bg-bronze-600/40" />
          <PrimaryButton
            asChild
            className="h-9 px-3 text-[11px] lg:h-10 lg:px-5 lg:text-[12px] tracking-[0.16em]"
          >
            {ALLOW_ON_DOMAIN_BOOKING ? (
              <Link to="/book">{CONSULT_CTA_LABEL}</Link>
            ) : isLevel("education_only") ? (
              <Link to="/family-capital-system">Learn the System</Link>
            ) : (
              <a href={APPROVED_CONSULT_URL}
              {...(CONSULT_URL_IS_EXTERNAL ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
                {CONSULT_CTA_LABEL}
              </a>
            )}
          </PrimaryButton>
        </div>

        {/* Mobile trigger + compact CTA */}
        <div className="flex shrink-0 items-center gap-2 lg:hidden">
          <PrimaryButton
            asChild
            className="hidden h-9 px-3 text-[11px] tracking-[0.16em] sm:inline-flex"
          >
            {ALLOW_ON_DOMAIN_BOOKING ? (
              <Link to="/book">Consultation</Link>
            ) : isLevel("education_only") ? (
              <Link to="/family-capital-system">The System</Link>
            ) : (
              <a href={APPROVED_CONSULT_URL}
              {...(CONSULT_URL_IS_EXTERNAL ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
                Consultation
              </a>
            )}
          </PrimaryButton>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger
              aria-label="Open menu"
              className="inline-flex h-11 w-11 items-center justify-center text-bronze-600 hover:text-bronze-400"
            >
              <Menu className="h-6 w-6" />
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-full max-w-sm border-l border-bronze-600/40 bg-navy-900 p-0 text-cream-50 [&>button]:hidden"
            >
              <SheetTitle className="sr-only">Navigation</SheetTitle>
              <SheetDescription className="sr-only">
                Currency Thoughts site navigation
              </SheetDescription>
              <div className="flex h-20 items-center justify-between border-b border-bronze-600/30 px-6">
                <Logo variant="light" />
                <button
                  type="button"
                  aria-label="Close menu"
                  onClick={() => setOpen(false)}
                  className="inline-flex h-11 w-11 items-center justify-center text-cream-50 hover:text-bronze-400"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <nav className="flex flex-col gap-4 p-8">
                {NAV_LINKS.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setOpen(false)}
                    className="font-sans text-[18px] font-semibold text-cream-50 transition-colors hover:text-bronze-400"
                    activeProps={{ className: "text-bronze-400" }}
                  >
                    {link.label}
                  </Link>
                ))}
                <Link
                  to="/login"
                  onClick={() => setOpen(false)}
                  className="mt-2 border-t border-bronze-600/20 pt-4 font-sans text-[18px] font-semibold text-cream-50/85 hover:text-bronze-400"
                >
                  Client Login
                </Link>
              </nav>
              <div className="flex flex-col gap-3 px-6 pb-10">
                <PrimaryButton asChild size="lg">
                  {ALLOW_ON_DOMAIN_BOOKING ? (
                    <Link to="/book" onClick={() => setOpen(false)}>
                      {CONSULT_CTA_LABEL}
                    </Link>
                  ) : isLevel("education_only") ? (
                    <Link to="/family-capital-system" onClick={() => setOpen(false)}>
                      Learn the System
                    </Link>
                  ) : (
                    <a
                      href={APPROVED_CONSULT_URL}
                      {...(CONSULT_URL_IS_EXTERNAL ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                      onClick={() => setOpen(false)}
                    >
                      {CONSULT_CTA_LABEL}
                    </a>
                  )}
                </PrimaryButton>
                <SecondaryButton asChild size="lg">
                  <Link to="/contact" onClick={() => setOpen(false)}>
                    Contact Us
                  </Link>
                </SecondaryButton>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}

export default Navbar;